import {
  RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT,
  RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS,
  RISK_EXTENSION_GAP_STATUS_UPDATES,
  RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED,
  RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED_BY,
  RISK_EXTENSION_GAP_STATUS_UPDATE_NOTE,
  RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
  RISK_EXTENSION_GAP_STATUS_UPDATE_SUBSTATUSES,
  SYSTEM_ZUS_GAP_SOURCE,
} from '@ctw/shared/api/fhir/system-urls';
import type { ResourceTypeString } from '@ctw/shared/api/fhir/types';
import type { FetchableResource } from '@ctw/shared/context/_internal/resource/resource-fetch';
import {
  isFetchableResourceType,
  isValidResourceId,
} from '@ctw/shared/context/_internal/resource/resource-mappings';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import { format, formatISO } from 'date-fns';
import type { MeasureReport, Resource } from 'fhir/r4';
import { compact } from 'lodash-es';
import { FHIRModel } from './fhir-model';

const payerMapping = {
  'homeward-payer-data': 'Homeward',
} as const;

export type RiskGapType = 'recapture' | 'suspect' | 'net-new' | 'unknown';
export type RiskGapSource = 'zus-recapture-model' | 'zus-suspect-model' | 'homeward-payer-data';

export interface RiskGapResource extends Resource {
  coefficient: number | null;
  confidenceLevel: number | null;
  evidence: Array<{ id: string; text: string; citations?: Array<string> }>;
  icd10Codes: Array<{ code: string; display: string }>;
  label: string;
  latestRecordedDate: string;
  hccCode: string;
  suspectedDate: string | null;
  type: RiskGapType;
  gapSource?: RiskGapSource;
  gapSourceList: Array<RiskGapSource>;
  upid: string;
  resourceType: 'RiskGap';
  extension?: MeasureReport['extension'];
  measureReport: MeasureReport;
  relatedRecords: Array<FetchableResource>;
}

const isPayerSource = (source: RiskGapResource['gapSource']): source is keyof typeof payerMapping =>
  !!source && source in payerMapping;

const getPayerDisplay = (source?: RiskGapResource['gapSource']): string =>
  payerMapping[source as keyof typeof payerMapping] ?? 'Payer';

const riskGapLegacyStatusMappings: Record<string, RiskGapStatus> = {
  'open-gap': 'open',
  'accepted-for-review': 'pending',
  'closed-gap': 'closed-unspecified',
} as const;

export const riskGapUserStatusOptions = [
  {
    label: 'Open',
    value: 'open',
  },
  {
    label: 'Pending',
    value: 'pending',
  },
  {
    label: 'Accepted for review',
    value: 'accepted-for-review',
  },
  {
    label: 'Dismissed',
    value: 'dismissed',
  },
] as const;

export type RiskGapUserStatus = (typeof riskGapUserStatusOptions)[number]['value'];

export const isRiskGapUserStatus = (status: string): status is RiskGapUserStatus =>
  riskGapUserStatusOptions.some((option) => option.value === status);

export const riskGapSystemStatusOptions = [
  {
    label: 'Expected to close',
    value: 'expected-to-close',
  },
  {
    label: 'Closed Unspecified',
    value: 'closed-unspecified',
  },
  {
    label: 'Closed by payer file',
    value: 'closed-by-payer-file',
  },
  {
    label: 'Closed by claims file',
    value: 'closed-by-claims-file',
  },
] as const;

export type RiskGapSystemStatus = (typeof riskGapSystemStatusOptions)[number]['value'];

const riskGapStatusOptions = [...riskGapUserStatusOptions, ...riskGapSystemStatusOptions] as const;
export const riskGapStatuses: NonEmptyArray<RiskGapStatus> = [
  ...riskGapUserStatusOptions.map((option) => option.value),
  ...riskGapSystemStatusOptions.map((option) => option.value),
] as NonEmptyArray<RiskGapStatus>;

export const isRiskGapSystemStatus = (status: string): status is RiskGapSystemStatus =>
  riskGapSystemStatusOptions.some((option) => option.value === status);

export type RiskGapStatus = RiskGapUserStatus | RiskGapSystemStatus;

export const isRiskGapStatus = (status: string): status is RiskGapStatus =>
  riskGapStatusOptions.some((option) => option.value === status);

export const riskGapDefaultSubstatuses = {
  open: [],
  pending: [
    {
      label: 'Sent to provider for review',
      value: 'pending-sent-to-provider-for-review',
    },
    {
      label: 'Condition being assessed',
      value: 'pending-condition-being-assessed',
    },
    {
      label: 'Other',
      value: 'pending-other',
    },
  ],
  'accepted-for-review': [
    {
      label: 'Sent to provider for review',
      value: 'accepted-for-review-sent-to-provider-for-review',
    },
    {
      label: 'Accepted by provider',
      value: 'accepted-for-review-accepted-by-provider',
    },
    {
      label: 'Condition being assessed',
      value: 'accepted-for-review-condition-being-assessed',
    },
    {
      label: 'Other',
      value: 'accepted-for-review-other',
    },
  ],
  dismissed: [
    {
      label: 'Acute in nature',
      value: 'acute-in-nature',
    },
    {
      label: 'Inaccurate or insufficient evidence',
      value: 'inaccurate-or-insufficient-evidence',
    },
    {
      label: 'Resolved',
      value: 'resolved',
    },
  ],
  'expected-to-close': [],
  'closed-by-payer-file': [],
  'closed-by-claims-file': [],
  'closed-unspecified': [],
} as const;

export interface RiskGapStatusUpdate {
  id: string;
  status: RiskGapStatus;
  substatuses: Array<string>;
  note: string;
  created: string;
  createdBy: string;
}

export interface Evidence {
  resourceId: string;
  resourceType: string;
  description: string;
  citations: Array<string>;
}

export const riskGapThumbsUpRatingReasons = [
  {
    label: 'Highlighted new information',
    value: 'highlighted-new-information',
  },
  {
    label: 'More accurate diagnosis code',
    value: 'more-accurate-diagnosis-code',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const;

export const riskGapThumbsDownRatingReasons = [
  {
    label: "There's a more appropriate code",
    value: 'theres-a-more-appropriate-code',
  },
  {
    label: 'Additional codes are needed',
    value: 'additional-codes-are-needed',
  },
  {
    label: 'More evidence is needed to confirm this code',
    value: 'more-evidence-is-needed-to-confirm-this-code',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const;

export class RiskGapModel extends FHIRModel<RiskGapResource> {
  public kind = 'MeasureReport' as const;

  public static fromUpidAndMeasureReport(upid: string, measureReport: MeasureReport): RiskGapModel {
    return new RiskGapModel({
      id: measureReport.id,
      resourceType: 'RiskGap' as const,
      upid,
      hccCode: extractHccCode(measureReport),
      label: extractHccDescription(measureReport),
      coefficient: extractCoefficient(measureReport),
      icd10Codes: extractIcd10Codes(measureReport),
      evidence: extractEvidence(measureReport),
      type: extractType(measureReport),
      gapSource: extractGapSource(measureReport),
      gapSourceList: extractGapSourceList(measureReport),
      confidenceLevel: extractConfidenceLevel(measureReport),
      latestRecordedDate: extractEvidenceDate(measureReport),
      suspectedDate: extractSuspectedDate(measureReport),
      relatedRecords: extractRelatedRecords(measureReport),
      measureReport,
    });
  }

  public get key(): string {
    return this.id;
  }

  public get resourceType(): ResourceTypeString {
    return 'MeasureReport';
  }

  public get evidence(): Array<Evidence> {
    return this.resource.evidence.map((evidence) => {
      const [resourceType, resourceId] = evidence.id.split('/');
      return {
        resourceId,
        resourceType,
        description: evidence.text || this.title,
        citations: evidence.citations || [],
      };
    });
  }

  public get relatedRecords(): Array<FetchableResource> {
    return this.resource.relatedRecords.map((relatedRecord) => {
      return {
        resourceId: relatedRecord.resourceId,
        resourceType: relatedRecord.resourceType,
      };
    });
  }

  public get statusUpdates(): Array<RiskGapStatusUpdate> {
    const statusUpdatesExtension = this.resource.measureReport.extension?.find(
      (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATES,
    );

    return (
      statusUpdatesExtension?.extension
        ?.map((statusUpdateExtension, index) => {
          const statusString =
            statusUpdateExtension.extension?.find(
              (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_STATUS,
            )?.valueCodeableConcept?.coding?.[0]?.code ?? '';

          return {
            id: `${this.id}|${index}`,
            status: isRiskGapStatus(statusString)
              ? statusString
              : (riskGapLegacyStatusMappings[statusString] ?? 'open'),
            substatuses:
              (statusUpdateExtension.extension
                ?.find(
                  (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_SUBSTATUSES,
                )
                ?.valueCodeableConcept?.coding?.map((coding) => coding.code)
                ?.filter(Boolean) as Array<string>) ?? [],
            note:
              statusUpdateExtension.extension?.find(
                (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_NOTE,
              )?.valueString ?? '',
            created: (() => {
              const createdExtension = statusUpdateExtension.extension?.find(
                (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED,
              );
              return createdExtension?.valueDateTime ?? createdExtension?.valueDate ?? '';
            })(),
            createdBy:
              statusUpdateExtension.extension?.find(
                (extension) => extension.url === RISK_EXTENSION_GAP_STATUS_UPDATE_CREATED_BY,
              )?.valueString ?? '',
          };
        })
        ?.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()) ?? []
    );
  }

  public get lastStatusUpdate(): RiskGapStatusUpdate {
    const lastUpdate = this.statusUpdates.at(-1);

    // Exclude status updates with `status` values we don't recognize because we don't know how to display those.
    if (!(lastUpdate && isRiskGapStatus(lastUpdate.status))) {
      return {
        id: `${this.id}|`,
        status: 'open',
        substatuses: [],
        note: '',
        created: this.resource.measureReport?.date ?? this.createdAt ?? formatISO(new Date()),
        createdBy: isPayerSource(this.gapSource)
          ? `${getPayerDisplay(this.gapSource)} Gap File`
          : 'Zus Automation',
      };
    }

    return lastUpdate;
  }

  public get lastStatusUpdateDate(): string {
    return this.lastStatusUpdate.created;
  }

  public get lastUpdatedByDisplay(): string {
    // A URL in this field indicates a status update made by a Zus system
    if (this.lastStatusUpdate.createdBy.startsWith('http')) {
      return 'Zus Automation';
    }

    return this.lastStatusUpdate.createdBy;
  }

  public get icd10Codes(): RiskGapResource['icd10Codes'] {
    return this.resource.icd10Codes;
  }

  public get hccCode(): string {
    return this.resource.hccCode;
  }

  public get title(): string {
    return this.resource.label;
  }

  public get type(): RiskGapType {
    return this.resource.type;
  }

  // Consider this function to be deprecated, given that a risk gap now has a list of gap sources
  // rather than a single source.  To maintain backwards compatbility (for now) we'll return
  // a source from the list using the some prioritization which was orginally done in SQL.
  public get gapSource(): RiskGapSource | undefined {
    if (this.resource.gapSourceList.includes('homeward-payer-data')) {
      return 'homeward-payer-data';
    }
    if (this.resource.gapSourceList.includes('zus-suspect-model')) {
      return 'zus-suspect-model';
    }
    if (this.resource.gapSourceList.includes('zus-recapture-model')) {
      return 'zus-recapture-model';
    }

    return undefined;
  }

  public get gapSourceList(): Array<RiskGapSource> {
    return this.resource.gapSourceList;
  }

  public get gapSourceListDisplay(): string {
    return [...this.gapSourceList]
      .sort()
      .map((source) => this.gapSourceToString(source))
      .join(', ');
  }

  public get gapSourceDisplay(): string {
    return this.gapSourceToString(this.gapSource);
  }

  private readonly gapSourceToString = (source: RiskGapSource | undefined): string => {
    switch (source) {
      case 'zus-suspect-model':
      case 'zus-recapture-model':
        return 'Zus';
      case 'homeward-payer-data':
        return getPayerDisplay(source);
      default:
        return '';
    }
  };

  public get rafScore(): number | null {
    return this.resource.coefficient;
  }

  public get rafScoreDisplay(): string {
    return this.rafScore?.toFixed(3) ?? 'unknown';
  }

  public get status(): RiskGapStatus {
    return this.lastStatusUpdate.status ?? 'open';
  }

  public get statusDisplay(): string {
    if (this.status === 'pending') {
      return `Pending ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'accepted-for-review') {
      return `Accepted for review ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'dismissed') {
      return `Dismissed ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
    }

    if (this.status === 'expected-to-close') {
      return 'Expected to Close by Encounter Diagnosis';
    }

    if (this.status === 'closed-by-payer-file') {
      return `Closed ${formatFHIRDate(this.lastStatusUpdateDate)} by Payer file`;
    }

    if (this.status === 'closed-unspecified') {
      return 'Closed';
    }

    if (this.status === 'closed-by-claims-file') {
      return `Closed ${formatFHIRDate(this.lastStatusUpdateDate)} by Claims file`;
    }

    return `Opened ${formatFHIRDate(this.lastStatusUpdateDate)} by ${this.lastUpdatedByDisplay}`;
  }

  public get substatuses(): Array<string> {
    return this.lastStatusUpdate.substatuses ?? [];
  }

  public get summary(): string {
    return ''; // TODO: Need to get summary from risk suspects and recapture when available in Snowflake
  }

  public get suspectedDisplay(): string | undefined {
    const { suspectedDate } = this.resource;
    if (suspectedDate) {
      return format(new Date(suspectedDate), 'MM/dd/yyyy');
    }
    return undefined;
  }

  public get lastDiagnosedDisplay(): string | undefined {
    const { latestRecordedDate } = this.resource;
    if (latestRecordedDate) {
      return format(new Date(latestRecordedDate), 'MM/dd/yyyy');
    }
    return undefined;
  }

  public get confidenceLevel(): number | null {
    return this.resource.confidenceLevel;
  }

  public get confidenceRating(): 'low' | 'medium' | 'high' | undefined {
    switch (this.confidenceLevel) {
      case 1:
      case 2:
        return 'low';
      case 3:
      case 4:
        return 'medium';
      case 5:
        return 'high';
      default:
        return undefined;
    }
  }

  // Note that this overrides the default implementation in FHIRModel, since the RiskGap models
  // don't store the UPID in the same way as other models.
  public get patientUPID(): string | undefined {
    return this.resource.upid;
  }
}

export const RISK_MEASURE_URL = 'http://zusapi.com/quality/measure/risk/CMSv28';

// Constants for FHIR extension URLs
const RA_SUSPECT_TYPE_URL = 'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-suspectType';
const RA_EVIDENCE_STATUS_DATE_URL =
  'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-evidenceStatusDate';
const RA_CONFIDENCE_LEVEL_URL =
  'http://hl7.org/fhir/us/davinci-ra/StructureDefinition/ra-confidenceLevel';
export const GAP_SOURCE_URL = 'https://zusapi.com/fhir/identifier/quality/riskgap/gap-source';
const HCC_CODE_SYSTEM_URL = 'http://terminology.hl7.org/CodeSystem/cmshcc' as const;
const ICD10_CODE_SYSTEM = 'http://hl7.org/fhir/sid/icd-10' as const;

export function extractHccCode(report: MeasureReport): string {
  return (
    report.group?.[0]?.code?.coding?.find((coding) => coding.system === HCC_CODE_SYSTEM_URL)
      ?.code ?? 'N/A'
  );
}

export function extractHccDescription(report: MeasureReport): string {
  return report.group?.[0]?.code?.coding?.[0]?.display ?? '';
}

export function extractEvidence(report: MeasureReport): RiskGapResource['evidence'] {
  if (!report?.evaluatedResource?.[0]) {
    return [];
  }

  return report.evaluatedResource.map((evidence) => ({
    id: evidence.reference ?? '',
    text: evidence.display ?? '',
  }));
}

export function extractType(report: MeasureReport): RiskGapResource['type'] {
  const reportGroupExtensions = report?.group?.[0]?.extension;
  const suspectTypeExtension = reportGroupExtensions?.find(
    (ext) => ext.url === RA_SUSPECT_TYPE_URL,
  );
  const type = suspectTypeExtension?.valueCodeableConcept?.coding?.[0]?.code;
  switch (type) {
    case 'historic':
      return 'recapture';
    case 'suspected':
      return 'suspect';
    case 'net-new':
      return 'net-new';
    default:
      return 'unknown';
  }
}

export function extractGapSource(report: MeasureReport): RiskGapResource['gapSource'] {
  const reportExtensions = report.extension;
  const sourceExtension = reportExtensions?.find((ext) => ext.url === GAP_SOURCE_URL);
  return sourceExtension?.valueCodeableConcept?.coding?.[0]?.code as RiskGapResource['gapSource'];
}

export function extractGapSourceList(report: MeasureReport): RiskGapResource['gapSourceList'] {
  const reportExtensions = report.extension;
  const sourceListExtensions = reportExtensions?.filter((ext) => ext.url === GAP_SOURCE_URL);
  if (!sourceListExtensions) {
    return [];
  }

  const sourceListCodes = sourceListExtensions
    ?.map(
      (source) =>
        source.valueCodeableConcept?.coding?.find(
          (coding) => coding.system === SYSTEM_ZUS_GAP_SOURCE,
        )?.code,
    )
    .filter(Boolean);
  return sourceListCodes as RiskGapResource['gapSourceList'];
}

export function extractEvidenceDate(report: MeasureReport): string {
  const reportGroupExtensions = report?.group?.[0]?.extension;
  const evidenceStatusDateExtension = reportGroupExtensions?.find(
    (ext) => ext.url === RA_EVIDENCE_STATUS_DATE_URL,
  );
  return evidenceStatusDateExtension?.valueDate ?? '';
}

export function extractCoefficient(report: MeasureReport): number | null {
  return report?.group?.[0]?.measureScore?.value ?? null;
}

export function extractIcd10Codes(report: MeasureReport): RiskGapResource['icd10Codes'] {
  const codes = report?.group?.[0]?.code?.coding;
  if (!codes) {
    return [];
  }
  return codes
    .filter((icd10) => icd10.system === ICD10_CODE_SYSTEM)
    .map((icd10) => ({
      code: icd10.code ?? '',
      display: icd10.display ?? '',
    }));
}

// SuspectedDate only applies to suspect risk gaps, where it is simply the date field on the
// MeasureReport.
export function extractSuspectedDate(report: MeasureReport): string {
  // If this isn't a suspect, return an empty string
  if (extractType(report) !== 'suspect') {
    return '';
  }
  return report.date ?? '';
}

export function extractRelatedRecords(report: MeasureReport): Array<FetchableResource> {
  const relatedRecordsExtension = report.extension?.find(
    (extension) => extension.url === RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECTS,
  );

  return (
    compact(
      relatedRecordsExtension?.extension?.map((relatedRecordExtension) => {
        if (relatedRecordExtension.url !== RISK_EXTENSION_GAP_RELATED_CLINICAL_OBJECT) {
          return null;
        }

        const [resourceType, resourceId] = relatedRecordExtension.valueReference?.reference?.split(
          '/',
        ) ?? ['', ''];
        if (!(isFetchableResourceType(resourceType) && isValidResourceId(resourceId))) {
          return null;
        }

        return {
          resourceType,
          resourceId,
        };
      }),
    ) ?? []
  );
}

export function extractConfidenceLevel(report: MeasureReport): number | null {
  if (extractType(report) === 'suspect') {
    const reportGroupExtensions = report?.group?.[0]?.extension;
    const confidenceExtension = reportGroupExtensions?.find(
      (ext) => ext.url === RA_CONFIDENCE_LEVEL_URL,
    );
    return confidenceExtension?.valueInteger ?? null;
  }
  // Return max confidence level for recapture gaps (or any other as)
  if (extractType(report) === 'recapture') {
    return 5;
  }
  if (extractType(report) === 'net-new') {
    return 5;
  }
  return null;
}
